// https://github.com/goldfire/howler.js/issues/1021

let canPlay = undefined;
let audioContext = null;
const stateChangeCallbacks = [];

// Track contexts we've already attached listeners to (prevents duplicates)
const listenedContexts = new WeakSet();

/**
 * Internal helper: notify callbacks safely
 * @param {string} state
 */
function notifyStateChange(state) {
	stateChangeCallbacks.forEach((callback) => {
		try {
			callback(state);
		} catch (e) {
			console.error('Error in state change callback:', e);
		}
	});
}

export default function canBrowserPlayAudio() {
	if (canPlay !== undefined) {
		return canPlay;
	}

	let ctx = null;

	try {
		if (typeof AudioContext !== 'undefined') {
			ctx = new AudioContext();
			console.log('AudioContext created successfully (test)');
		} else if (typeof webkitAudioContext !== 'undefined') {
			// eslint-disable-next-line no-undef
			ctx = new webkitAudioContext();
			console.log('webkitAudioContext created successfully (test)');
		}
	} catch (e) {
		console.error('Failed to create AudioContext:', e);
	}

	// Capability check only (do NOT store globally, do NOT add listeners)
	canPlay = !!(
		ctx &&
		(ctx.createGainNode !== undefined || ctx.createGain !== undefined)
	);

	// Close the test context to avoid statechange side effects / leaks
	if (ctx && typeof ctx.close === 'function') {
		try {
			ctx.close();
		} catch (e) {
			// Some browsers may throw if closing immediately; ignore.
		}
	}

	console.log('Can browser play audio:', canPlay);
	return canPlay;
}

/**
 * Register the actual SoundSource AudioContext for monitoring and resuming
 * This should be called by initializeSoundSource after creating the context
 * @param {AudioContext} context - The AudioContext used by SoundSource
 */
export function registerAudioContext(context) {
	if (!context) return;

	audioContext = context;
	console.log('Registered SoundSource AudioContext for monitoring');

	// Avoid registering multiple listeners for the same context
	if (listenedContexts.has(context)) return;
	listenedContexts.add(context);

	context.addEventListener('statechange', () => {
		console.log('SoundSource AudioContext state changed to:', context.state);
		notifyStateChange(context.state);
	});
}

/**
 * Get the AudioContext instance
 * @returns {AudioContext|null}
 */
export function getAudioContext() {
	return audioContext;
}

/**
 * Check if AudioContext is suspended
 * @returns {boolean}
 */
export function isAudioContextSuspended() {
	if (!audioContext) {
		return false;
	}
	return audioContext.state === 'suspended';
}

/**
 * Resume the AudioContext (requires user interaction)
 * @returns {Promise<void>}
 */
export function resumeAudioContext() {
	if (!audioContext) {
		console.warn('No AudioContext available to resume');
		return Promise.resolve();
	}
	
	if (audioContext.state === 'running') {
		console.log('AudioContext is already running');
		return Promise.resolve();
	}
	
	return audioContext.resume()
		.then(() => {
			console.log('AudioContext resumed successfully, new state:', audioContext.state);
		})
		.catch((e) => {
			console.error('Failed to resume AudioContext:', e);
			throw e;
		});
}

/**
 * Register a callback for AudioContext state changes
 * @param {function} callback - Function that receives the new state as parameter
 * @returns {function} Unsubscribe function
 */
export function onAudioContextStateChange(callback) {
	stateChangeCallbacks.push(callback);
	
	// Return unsubscribe function
	return () => {
		const index = stateChangeCallbacks.indexOf(callback);
		if (index > -1) {
			stateChangeCallbacks.splice(index, 1);
		}
	};
}
